import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Mic, Target, Lightbulb, Users, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  useEffect(() => {
    if (inviteToken) {
      axios.get(`${API}/org/invite/${inviteToken}`)
        .then(res => {
          setInviteInfo(res.data);
          setEmail(res.data.email);
        })
        .catch(() => setError('Invalid or expired invitation link'));
    }
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    const result = await register(email, password, name, termsAccepted, inviteToken, companyName.trim() || null);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="register-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A343D] p-14 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#0E4E5A] opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full bg-[#7CD9CC] opacity-10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-10 w-10 object-contain" />
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              PipeGuide
            </span>
          </div>
        </div>

        <div className="space-y-6 relative">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Elevate Your<br />Sales Conversations
          </h1>
          <p className="text-white/60 text-lg max-w-md">
            Join top sales professionals using AI to improve qualification call quality.
          </p>

          <div className="space-y-3 pt-6">
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-5 py-3.5 text-white/90">
              <Target className="w-5 h-5 text-[#7CD9CC] shrink-0" />
              <span>BANT, MEDDIC, SPIN frameworks built-in</span>
            </div>
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-5 py-3.5 text-white/90">
              <Lightbulb className="w-5 h-5 text-[#7CD9CC] shrink-0" />
              <span>AI-suggested questions in real-time</span>
            </div>
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-5 py-3.5 text-white/90">
              <Mic className="w-5 h-5 text-[#7CD9CC] shrink-0" />
              <span>Live transcription and analysis</span>
            </div>
          </div>
        </div>

        <p className="text-white/40 text-sm relative">
          Free to try. No credit card required.
        </p>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <Card className="w-full max-w-md border-zinc-200 shadow-sm rounded-2xl">
          <CardHeader className="space-y-1 pt-8 px-8">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-8 w-8 object-contain" />
              <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                PipeGuide
              </span>
            </div>
            <CardTitle className="text-3xl tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {inviteInfo ? 'Join your team' : 'Create an account'}
            </CardTitle>
            <CardDescription>
              {inviteInfo 
                ? `You've been invited to join ${inviteInfo.org_name} as ${inviteInfo.role}`
                : 'Get started with PipeGuide for free'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {inviteInfo && (
                <div className="bg-brand-muted border border-brand-accent text-brand-primary px-4 py-3 rounded-md flex items-center gap-2" data-testid="invite-banner">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Joining <strong>{inviteInfo.org_name}</strong> as <strong>{inviteInfo.role}</strong></span>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2" data-testid="register-error">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="rounded-md h-11"
                  data-testid="register-name-input"
                />
              </div>

              {/* Only show company-name field for fresh signups, not for invited reps joining an existing org */}
              {!inviteInfo && (
                <div className="space-y-2">
                  <Label htmlFor="company">
                    Company Name <span className="text-zinc-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="rounded-md h-11"
                    data-testid="register-company-input"
                  />
                  <p className="text-xs text-zinc-500">You can update this later in Settings.</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-md h-11"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Create Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Choose a password (min. 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-md h-11 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`rounded-md h-11 pr-10 ${confirmPassword && password !== confirmPassword ? 'border-red-300 focus-visible:ring-red-300' : ''}`}
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-600" data-testid="password-mismatch-hint">Passwords do not match</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 rounded border-zinc-300"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-xs text-zinc-600 leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-brand-primary hover:underline" target="_blank">Terms of Service</Link>,{' '}
                  <Link to="/privacy" className="text-brand-primary hover:underline" target="_blank">Privacy Policy</Link>, and{' '}
                  <Link to="/data-processing" className="text-brand-primary hover:underline" target="_blank">Data Processing Policy</Link>.
                  I understand that conversations will be recorded and analyzed by AI.
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-bold bg-brand-primary hover:bg-brand-hover rounded-xl shadow-md transition-transform hover:-translate-y-0.5"
                disabled={loading || !termsAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-zinc-400">or</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl border-zinc-200 hover:bg-zinc-50"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                if (inviteToken) {
                  localStorage.setItem('pipeguide_invite_token', inviteToken);
                }
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-register-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {inviteInfo ? 'Join with Google' : 'Sign up with Google'}
            </Button>

            <div className="mt-6 text-center text-sm">
              <span className="text-zinc-500">Already have an account? </span>
              <Link to="/login" className="text-brand-primary hover:text-brand-primary font-medium" data-testid="login-link">
                Sign in
              </Link>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-zinc-400">
              <Link to="/terms" className="hover:text-zinc-600 transition-colors" data-testid="footer-terms">Terms</Link>
              <span>·</span>
              <Link to="/privacy" className="hover:text-zinc-600 transition-colors" data-testid="footer-privacy">Privacy</Link>
              <span>·</span>
              <Link to="/security" className="hover:text-zinc-600 transition-colors" data-testid="footer-security">Security</Link>
              <span>·</span>
              <Link to="/sub-processors" className="hover:text-zinc-600 transition-colors" data-testid="footer-subprocessors">Sub-processors</Link>
              <span>·</span>
              <Link to="/data-processing" className="hover:text-zinc-600 transition-colors" data-testid="footer-data">Data Policy</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
